import {JobNodes} from "@cm/job-nodes/job-nodes"
import {Utility} from "@cm/job-nodes/utility"
import type {InputMapsInfo, MaterialDetails} from "#material-maps-exporter/core/types"
import {imageProcessingGraphForPconExport} from "#material-maps-exporter/targets/pcon/image-processing-graph"
import type {PconExportConfig, PconExportTextureType} from "#material-maps-exporter/targets/pcon/types"
import {PCON_EXPORT_EXTENSION, PCON_EXPORT_DEFAULT_TEXTURE_TYPES, PCON_EXPORT_OPACITY} from "#material-maps-exporter/targets/pcon/types"
import {imageProcessingTask} from "@cm/job-nodes/image-processing"
import {TextureType} from "@cm/material-nodes"
import {IsNonNull, uuid4 as uuid} from "@cm/utils"

const CM_TO_M = 0.01

const PCON_MATERIAL_INFO_XML_APP_KEY = "P-CAT2"
const PCON_MATERIAL_INFO_XML_APP_VERSION = "2.8.8"
const PCON_MATERIAL_INFO_XML_VERSION = "1.0"
// pCon uses two spaces as the neutral/default language identifier.
const PCON_DEFAULT_LANG = "  "
const PCON_MATERIAL_INFO_XML_FILENAME = "material_info.xml"

function escapeXml(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;")
}

export type PconMaterialInfoXmlParams = {
    /** Short, human-readable name of the material (used as the short description). */
    materialName: string
    /** Manufacturer/organization identifier, e.g. "EKORNES". */
    manufacturerId?: string
    /** Manufacturer/organization display name, e.g. "Ekornes". */
    manufacturerName?: string
    /** Public catalog link to the material. */
    link?: string
    /** pCon material type, defaults to "Material". */
    materialType?: string
}

/**
 * Builds the `material_info.xml` metadata document that ships alongside the exported pCon material maps.
 * Mirrors the structure produced by pCon.catalog (see an exported material's `material_info.xml`).
 */
export function pconMaterialInfoXml({materialName, manufacturerId, manufacturerName, link, materialType = "Material"}: PconMaterialInfoXmlParams): string {
    const lines = [
        `<?xml version="1.0" encoding="UTF-8"?>`,
        `<material_info>`,
        `\t<versionInfo appKey="${PCON_MATERIAL_INFO_XML_APP_KEY}" appVersion="${PCON_MATERIAL_INFO_XML_APP_VERSION}" materialXmlVersion="${PCON_MATERIAL_INFO_XML_VERSION}"/>`,
        `\t<material_item materialType="${escapeXml(materialType)}">`,
    ]

    if (manufacturerId !== undefined || manufacturerName !== undefined) {
        lines.push(`\t\t<manufacturer id="${escapeXml(manufacturerId ?? "")}">`)
        lines.push(`\t\t\t<name lang="${PCON_DEFAULT_LANG}">${escapeXml(manufacturerName ?? "")}</name>`)
        lines.push(`\t\t</manufacturer>`)
    }

    if (link !== undefined) {
        lines.push(`\t\t<link>${escapeXml(link)}</link>`)
    }

    lines.push(`\t\t<description type="short">`)
    lines.push(`\t\t\t<text lang="${PCON_DEFAULT_LANG}">${escapeXml(materialName)}</text>`)
    lines.push(`\t\t</description>`)

    lines.push(`\t</material_item>`)
    lines.push(`</material_info>`)

    return lines.join("\n") + "\n"
}

function extensionForFormat(format: PconExportConfig["format"]): "jpg" | "png" {
    switch (format) {
        case "jpeg":
            return "jpg"
        case "png":
            return "png"
    }
    throw new Error(`Unsupported format: ${format}`)
}

const exportMatFileIdForDefaultTextureType: Record<(typeof PCON_EXPORT_DEFAULT_TEXTURE_TYPES)[number], string> = {
    [TextureType.Diffuse]: "tex image",
    [TextureType.Normal]: "bumps",
    [TextureType.Metalness]: "metallic image",
    [TextureType.Roughness]: "roughness image",
}

function shouldExportOpacity(mapsInfo: InputMapsInfo) {
    return (
        (mapsInfo[TextureType.Mask] !== undefined && mapsInfo[TextureType.Mask].data.type !== "ShaderNodeRGB") ||
        mapsInfo[TextureType.Transmission] !== undefined
    )
}

export function jobGraphForPconExport(
    materialDetails: MaterialDetails,
    mapsInfo: InputMapsInfo,
    exportConfig: PconExportConfig,
    platformVersion: string,
    exportName: string,
) {
    const exportOpacity = shouldExportOpacity(mapsInfo)
    const imageProcessingGraph = imageProcessingGraphForPconExport(exportConfig, mapsInfo, exportOpacity)
    const imgProcTask = JobNodes.task(imageProcessingTask, {
        input: JobNodes.value({graph: imageProcessingGraph}),
    })

    const exportTextureFileNameForTextureType = (textureType: PconExportTextureType) => {
        return `${exportName}_${textureType}`
    }

    const scaleX = 1.0 / (exportConfig.commonWidthCm * CM_TO_M)
    const scaleY = 1.0 / (exportConfig.commonHeightCm * CM_TO_M)

    const mapExtension = extensionForFormat(exportConfig.format)

    const exportMatFileContent =
        [
            "type common",
            ...PCON_EXPORT_DEFAULT_TEXTURE_TYPES.map(
                (type) => `${exportMatFileIdForDefaultTextureType[type]} ${mapExtension} ${exportTextureFileNameForTextureType(type)}`,
            ),
            exportOpacity ? `opacity image ${mapExtension} ${exportTextureFileNameForTextureType(PCON_EXPORT_OPACITY)}` : null,
            `scale ${scaleX} ${scaleY} 1`,
        ]
            .filter(IsNonNull)
            .join("\n") + "\n"

    const zipTask = JobNodes.task(Utility.Zip.task, {
        input: JobNodes.struct({
            filename: JobNodes.value(exportName),
            extension: JobNodes.value(PCON_EXPORT_EXTENSION),
            destination: JobNodes.value<Utility.Zip.Destination>({
                type: "taskOutputOnly",
                customerId: materialDetails.organization.legacyId,
            }),
            root: Utility.Zip.JobNodes.folder({
                ...Object.fromEntries(
                    [
                        PCON_EXPORT_DEFAULT_TEXTURE_TYPES.map((type) => {
                            return [`${exportTextureFileNameForTextureType(type)}.${mapExtension}`, Utility.Zip.JobNodes.file(JobNodes.get(imgProcTask, type))]
                        }),
                        exportOpacity
                            ? [
                                  [
                                      `${exportTextureFileNameForTextureType(PCON_EXPORT_OPACITY)}.${mapExtension}`,
                                      Utility.Zip.JobNodes.file(JobNodes.get(imgProcTask, PCON_EXPORT_OPACITY)),
                                  ],
                              ]
                            : [],
                    ].flat(),
                ),
                [`${exportName}.mat`]: Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.text(exportMatFileContent)),
                [PCON_MATERIAL_INFO_XML_FILENAME]: Utility.Zip.JobNodes.file(
                    Utility.Zip.JobNodes.text(
                        pconMaterialInfoXml({
                            materialName: exportName,
                            manufacturerName: materialDetails.organization.name,
                        }),
                    ),
                ),
                "id.txt": Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.text(uuid())),
                "meta.txt": Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.text("Web")),
                "name.csv": Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.text(`any;${exportName}`)),
            }) as unknown as JobNodes.TypedDataNode<Utility.Zip.Folder>,
        }),
    })

    const updateMaterialOutputTask = JobNodes.task(Utility.MaterialOutput.task, {
        input: JobNodes.struct({
            operation: JobNodes.value("update" as const),
            fields: JobNodes.struct({
                materialId: JobNodes.value(materialDetails.id),
                dataObject: JobNodes.get(zipTask, "zipFile"),
            }),
        }),
    })

    return JobNodes.jobGraph(JobNodes.list([updateMaterialOutputTask, zipTask]), {platformVersion})
}
